import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Divider, Typography } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import SavingOverlay from '@sharedCR/coreDesignSystem/SavingOverlay';
import SectionTabs from 'app/shared/components/common/SectionTabs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { isString } from 'lodash';
import PropTypes from 'prop-types';

import usePrevious from 'hooks/usePrevious';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';

import { selectIsMobile, selectIsTablet } from 'store/global/selectors';

import NavigationOption from './components/NavigationOption';
import {
  contentStyles,
  contentWrapperStyles,
  controlsWrapperStyles,
  dividerStyles,
  mobileNavigationStyles,
  navigationStyles,
  titleStyles,
  wrapperStyles,
} from './EntityFormModalMain.styles';
import { EntityFormModalMainTestIds } from './EntityFormModalMain.testIds';

const defaultTabs = [];

const EntityFormModalMain = ({
  title,
  onClose,
  onRequestClose,
  isFormChanged,
  setIsFormChanged,
  isData,
  isDataLoading,
  isLoading: passedIsLoading,
  setIsLoading,
  customControls,
  Content,
  additionalNavigationContent,
  tabs = defaultTabs,
  value,
  additionalProps,
  setIsContentLoading,
  isHiddenNavigationTabs = false,
  setIsNestedEntityEditActive,
  withApply = false,
}) => {
  const hasRenderedContentRef = useRef(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAllowed, setIsSavingAllowed] = useState(true);
  const [onSave, setOnSave] = useState(null);

  if (!isDataLoading) {
    hasRenderedContentRef.current = true;
  }

  const isMobile = useSelector(selectIsMobile);
  const isTablet = useSelector(selectIsTablet);
  const isMobileOrTablet = isMobile || isTablet;

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const onNavigationChange = useCallback(
    (event, selectedOption) => {
      const selectedTab = tabs?.find(tab => tab.value === selectedOption?.value);
      selectedTab?.onClick?.();
    },
    [tabs],
  );

  const navigationOptions = useMemo(
    () =>
      tabs?.map(tab => ({
        value: tab.value,
        name: tab.displayName || (isString(tab.label) ? tab.label : tab.value),
        isNeeded: !!tab.isNeeded,
      })) || [],
    [tabs],
  );

  const selectedNavigationOption = useMemo(
    () => navigationOptions.find(option => option.value === value) || null,
    [navigationOptions, value],
  );

  const isLoading = usePrevious(passedIsLoading) === undefined || passedIsLoading;

  const onCommit = useCallback(
    keepOpen => {
      if (!isSavingAllowed || !onSave) {
        return;
      }

      setIsSaving(true);
      onSave({ keepOpen }).finally(() => {
        setIsSaving(false);
      });
    },
    [isSavingAllowed, onSave],
  );

  const onClickApply = useCallback(() => onCommit(true), [onCommit]);

  const onClickSave = useCallback(() => onCommit(false), [onCommit]);

  const isCommitDisabled = isSaving || !isFormChanged || isLoading || !isSavingAllowed;
  // Apply makes the footer carry three tiers, and an outlined Cancel next to an outlined Apply is
  // indistinguishable while one discards and the other persists — drop Cancel to text and let Save
  // name where it lands. Footers without Apply keep the two-button styling and the plain label.
  const cancelVariant = withApply ? 'text' : 'outlined';
  const saveLabel = withApply ? 'Save and Exit' : 'Save';

  const controls = useMemo(() => {
    if (customControls) {
      return customControls;
    }

    return (
      <>
        <Button
          className="control control_cancel"
          data-testid={EntityFormModalMainTestIds.CANCEL_BUTTON}
          variant={cancelVariant}
          onClick={onRequestClose}
        >
          Cancel
        </Button>

        {withApply && (
          <Button
            className="control control_apply"
            data-testid={EntityFormModalMainTestIds.APPLY_BUTTON}
            disabled={isCommitDisabled}
            variant="outlined"
            onClick={onClickApply}
          >
            Apply
          </Button>
        )}

        <Button
          className="control control_save"
          color="primary"
          data-testid={EntityFormModalMainTestIds.SAVE_BUTTON}
          disabled={isCommitDisabled}
          onClick={onClickSave}
        >
          {saveLabel}
        </Button>
      </>
    );
  }, [
    cancelVariant,
    customControls,
    isCommitDisabled,
    onClickApply,
    onClickSave,
    onRequestClose,
    saveLabel,
    withApply,
  ]);

  return (
    <Box className="main" sx={wrapperStyles}>
      {title && (
        <>
          {isString(title) ? (
            <Typography className="title" sx={titleStyles} variant="bold32">
              {title}
            </Typography>
          ) : (
            title
          )}

          <Divider className="divider" sx={dividerStyles} />
        </>
      )}

      {!isHiddenNavigationTabs && isMobileOrTablet && tabs && value && (
        <Box sx={mobileNavigationStyles}>
          <AutocompleteCDS
            disableClearable
            OptionContent={NavigationOption}
            dataTestId={EntityFormModalMainTestIds.NAVIGATION_SELECT}
            name="mobile-navigation"
            options={navigationOptions}
            value={selectedNavigationOption}
            onChange={onNavigationChange}
          />
        </Box>
      )}

      <Box sx={contentStyles}>
        {!isHiddenNavigationTabs && !isMobileOrTablet && (
          <>
            <Box
              className="navigation-tabs-wrapper"
              sx={navigationStyles}
              onMouseLeave={onResetTooltipState}
              onMouseOver={onMouseOver}
            >
              <TruncatedProvider />

              {additionalNavigationContent}

              {tabs && value && <SectionTabs tabs={tabs} value={value} />}
            </Box>

            <Divider orientation="vertical" sx={dividerStyles} />
          </>
        )}

        <Box sx={contentWrapperStyles}>
          <SavingOverlay isOpen={isLoading || isDataLoading} />
          {hasRenderedContentRef.current && (
            <div className={isDataLoading ? 'hidden' : 'contents'}>
              <Content
                additionalProps={additionalProps}
                isData={isData}
                isLoading={isLoading}
                isSaving={isSaving}
                isSavingAllowed={isSavingAllowed}
                setIsContentLoading={setIsContentLoading}
                setIsFormChanged={setIsFormChanged}
                setIsLoading={setIsLoading}
                setIsNestedEntityEditActive={setIsNestedEntityEditActive}
                setIsSaving={setIsSaving}
                setIsSavingAllowed={setIsSavingAllowed}
                setOnSave={setOnSave}
                onClose={onClose}
              />
            </div>
          )}
        </Box>
      </Box>

      <Divider className="divider" sx={dividerStyles} />

      <Box
        className="controls-wrapper"
        id="entity-form-modal-main-controls"
        sx={controlsWrapperStyles}
      >
        {controls}
      </Box>
    </Box>
  );
};

EntityFormModalMain.propTypes = {
  Content: PropTypes.elementType.isRequired,
  additionalProps: PropTypes.object.isRequired,
  isData: PropTypes.bool.isRequired,
  setIsContentLoading: PropTypes.func.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  tabs: PropTypes.array,
  value: PropTypes.string,
  customControls: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  title: PropTypes.node,
  additionalNavigationContent: PropTypes.node,
  isHiddenNavigationTabs: PropTypes.bool,
  setIsNestedEntityEditActive: PropTypes.func,
  withApply: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default memo(withLoading(EntityFormModalMain));
